import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/router";
import { useMeasureRef } from "lib/hooks/useMeasureRef";
import { generateCrumbs } from "./generate-crumbs";
import { AnimatePresence, motion } from "motion/react";
import { CrumbRule } from "./crumb-rule";
import { countFoldedCrumbs } from "./count-folded-crumbs";
import BreadcrumbFoldMenu from "./BreadcrumbFoldMenu";
import ChevronRight from "components/icons/24/actions/chevron-right.svg";
import BreadcrumbDefault from "components/elements/Breadcrumbs/renderers/BreadcrumbDefault";
import BreadcrumbAsset from "components/elements/Breadcrumbs/renderers/BreadcrumbAsset";
import BreadcrumbView from "components/elements/Breadcrumbs/renderers/BreadcrumbView";
import BreadcrumbElement from "components/elements/Breadcrumbs/renderers/BreadcrumbElement";
import BreadcrumbAssetPageType from "components/elements/Breadcrumbs/renderers/BreadcrumbAssetPageType";
import BreadcrumbWorkspace from "components/elements/Breadcrumbs/renderers/BreadcrumbWorkspace";
import BreadcrumbIssue from "components/elements/Breadcrumbs/renderers/BreadcrumbIssue";
import BreadcrumbAssetVersion from "components/elements/Breadcrumbs/renderers/BreadcrumbAssetVersion";
import BreadcrumbRule from "components/elements/Breadcrumbs/renderers/BreadcrumbRule";

const anyParam = /\[\w+\]/;

// Crumbs wider than this may shrink (cutting their name) when folding is not
// enough; shorter ones like "BIM" or "Versions" always show in full.
const shrinkableCrumbWidth = 120;

// Long names shorten down to about this width (roughly 22 characters, keeping
// the ending) before any level folds into the "…" menu.
const shortenedCrumbWidth = 180;

const crumbFadeMilliseconds = 300;

const rules: CrumbRule[] = [
  { segments: [/projects/, anyParam], component: null },
  {
    segments: [/workspaces/],
    component: () => (
      <BreadcrumbDefault
        segments={["Workspaces"]}
        params={[]}
        href={{ pathname: "/projects" }}
      />
    ),
  },
  {
    segments: [/accounts/],
    component: () => (
      <BreadcrumbDefault
        segments={["Account"]}
        params={[]}
        href={{ pathname: "/projects" }}
      />
    ),
  },
  { segments: [/elements/, /\[elementId\]/], component: BreadcrumbElement },
  {
    segments: [/\[assetPageType\]/],
    component: BreadcrumbAssetPageType,
  },
  { segments: [/\[assetId\]/], component: BreadcrumbAsset },
  { segments: [/\[versionId\]/], component: BreadcrumbAssetVersion },
  { segments: [/\[issueId\]/], component: BreadcrumbIssue },
  { segments: [/\[workspaceId\]/], component: BreadcrumbWorkspace },
  { segments: [/\[viewId\]/], component: BreadcrumbView },
  { segments: [/\[ruleId\]/], component: BreadcrumbRule },
  {
    segments: [/members/],
    component: (p) => (
      <BreadcrumbDefault segments={["Members"]} params={[]} href={p.href} />
    ),
  },
  {
    segments: [/selected/],
    component: null,
  },
];

export default function Breadcrumbs() {
  const router = useRouter();
  const measuredCrumbRefs = useRef<HTMLDivElement[]>([]);
  const [availableWidth, setAvailableWidth] = useState(0);
  const [crumbWidths, setCrumbWidths] = useState<number[]>([]);

  const crumbs = useMemo(
    () => generateCrumbs(rules, router.pathname, router.query),
    [router.pathname, router.query],
  );

  const foldedCount =
    availableWidth > 0 && crumbWidths.length === crumbs.length
      ? countFoldedCrumbs(
          crumbWidths.map((width) =>
            width > shrinkableCrumbWidth
              ? Math.min(width, shortenedCrumbWidth)
              : width,
          ),
          availableWidth,
        )
      : 0;
  const foldedCrumbs = crumbs.slice(1, foldedCount + 1);

  const setContainerMeasureRef = useMeasureRef((bb) =>
    setAvailableWidth(bb.width),
  );

  // Each crumb's full width, measured from an invisible copy of the whole
  // trail, decides how many crumbs fold into the "…" menu.
  const setMeasuredTrailRef = useMeasureRef(() => measureCrumbWidths());

  function measureCrumbWidths() {
    const widths = crumbs.map(
      (_, i) =>
        measuredCrumbRefs.current[i]?.getBoundingClientRect().width ?? 0,
    );
    setCrumbWidths((previousWidths) =>
      haveSameWidths(previousWidths, widths) ? previousWidths : widths,
    );
  }

  useLayoutEffect(() => {
    measureCrumbWidths();
  }, [crumbs]);

  return (
    <div
      ref={setContainerMeasureRef}
      className="relative flex min-w-0 flex-1 cursor-default items-center gap-2 overflow-hidden font-mono text-xs text-gray-600"
    >
      <div
        ref={setMeasuredTrailRef}
        aria-hidden
        className="pointer-events-none invisible absolute left-0 top-0 flex w-max items-center"
      >
        {crumbs.map((crumb, i, { length }) => (
          <div
            key={i}
            ref={(el) => {
              measuredCrumbRefs.current[i] = el;
            }}
          >
            <crumb.component
              segments={crumb.segments}
              params={crumb.params}
              href={i < length - 1 && crumb.href}
            />
          </div>
        ))}
      </div>
      <AnimatePresence>
        {crumbs.map((crumb, i, { length }) => {
          const isFolded = i > 0 && i <= foldedCount;
          const isFirstFolded = i === 1 && foldedCount > 0;
          if (isFolded && !isFirstFolded) return null;
          return (
            <React.Fragment key={i}>
              {i > 0 && (
                <motion.span
                  className="shrink-0"
                  initial={{ visibility: "hidden" }}
                  animate={{ visibility: "visible" }}
                  exit={{ visibility: "hidden" }}
                  transition={{ delay: 0.1 }}
                >
                  <ChevronRight className="w-5" />
                </motion.span>
              )}
              {isFirstFolded ? (
                <BreadcrumbFoldMenu crumbs={foldedCrumbs} />
              ) : (
                <motion.div
                  className={
                    crumbWidths[i] > shrinkableCrumbWidth
                      ? "relative min-w-0"
                      : "relative shrink-0"
                  }
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{
                    duration: crumbFadeMilliseconds / 1000,
                    ease: "easeOut",
                  }}
                >
                  {i === length - 1 && (
                    // Shared between pages, so the highlight slides across
                    // to the new current page.
                    <motion.div
                      layoutId="breadcrumb-current-page"
                      className="absolute inset-0 rounded-md bg-gray-200"
                      transition={{
                        duration: crumbFadeMilliseconds / 1000,
                        ease: "easeOut",
                      }}
                    />
                  )}
                  <div className="relative min-w-0">
                    <crumb.component
                      segments={crumb.segments}
                      params={crumb.params}
                      href={i < length - 1 && crumb.href}
                    />
                  </div>
                </motion.div>
              )}
            </React.Fragment>
          );
        })}
      </AnimatePresence>
    </div>
  );
}

function haveSameWidths(first: number[], second: number[]) {
  return (
    first.length === second.length &&
    first.every((width, i) => width === second[i])
  );
}
